import {EditOutlined} from '@ant-design/icons';
import {Button, Table} from 'antd';
import {ColumnProps} from 'antd/es/table';
import {
  AccountExtraFeeRegion,
  AccountSteps,
  MasterAccountFieldsFragment,
  UpdateMasterAccountInput,
  useUpdateMasterAccountMutation
} from 'api/generated';
import {AnchorLink, PropertyList, Stack, TableCard} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, SubmitButton} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {formatLanguage, formatWebsite} from 'lib/formaters';
import {omitEmpty} from 'lib/omitEmpty';
import React, {useState} from 'react';
import {
  formatBigNumber,
  formatExtraFee,
  formatFee,
  formatFlatFee,
  checkIfRegional
} from 'routes/Account/Billing/utils';
import {MasterAccountDefaultsForm} from './MasterAccountDefaultsForm';
import {ScrollToError} from 'components/Form/ScrollToError';
import {businessCategories} from 'lib/businessCategories';
import {ExtendedAccountFees} from 'routes/Account/Billing/types';

interface MasterAccountDefaultsProps {
  cardProps?: any;
  formProps?: any;
  masterAccount: MasterAccountFieldsFragment;
}

export const MasterAccountDefaults: React.FC<MasterAccountDefaultsProps> = ({
  cardProps,
  formProps,
  masterAccount
}) => {
  const [isEditing, setEditing] = useState(false);
  const {isAdmin} = useAccessLevel();
  const [updateMasterAccount] = useUpdateMasterAccountMutation();

  if (!masterAccount) return <TableCard {...cardProps} title="Account defaults" />;

  const stepsColumns: Array<ColumnProps<AccountSteps>> = [
    {
      title: 'Starting from',
      dataIndex: 'from',
      align: 'right',
      render: formatBigNumber
    },
    {
      title: 'Card transaction fee',
      dataIndex: 'trxFee',
      render: formatFee
    },
    {
      title: 'Bizum transaction fee',
      dataIndex: 'bizumTrxFee',
      render: formatFee
    },
    {
      title: 'Flat fee per card transaction',
      dataIndex: 'trxFlatFee',
      render: formatFlatFee
    },
    {
      title: 'Flat fee per bizum transaction',
      dataIndex: 'bizumTrxFlatFee',
      render: formatFlatFee
    }
  ];

  const initialValues: UpdateMasterAccountInput = {
    id: masterAccount.id,
    accountDefaults: {
      billingPlan: masterAccount.accountDefaults?.billingPlan,
      fees: {
        trxFee: masterAccount.accountDefaults?.fees?.trxFee,
        trxFlatFee: masterAccount.accountDefaults?.fees?.trxFlatFee,
        highRiskFee: masterAccount.accountDefaults?.fees?.highRiskFee,
        dailyPlatformFee: masterAccount.accountDefaults?.fees?.dailyPlatformFee,
        dailyExtraSeatsFee: masterAccount.accountDefaults?.fees?.dailyExtraSeatsFee,
        extra: masterAccount.accountDefaults?.fees?.extra || [],
        steps: masterAccount.accountDefaults?.fees?.steps || []
      },
      business: {
        mcc: masterAccount.accountDefaults?.business?.mcc,
        website: masterAccount.accountDefaults?.business?.website,
        confirmWebsite: masterAccount.accountDefaults?.business?.confirmWebsite,
        servicesDescription: masterAccount.accountDefaults?.business?.servicesDescription,
        confirmServicesDescription:
          masterAccount.accountDefaults?.business?.confirmServicesDescription
      },
      language: masterAccount.accountDefaults?.language || 'en'
    }
  };

  const handleSubmit = async (input: UpdateMasterAccountInput) => {
    // Set confirmWebsite and confirmServicesDescription based on field values
    if (input.accountDefaults?.business) {
      input.accountDefaults.business.confirmWebsite = !!input.accountDefaults.business.website;
      input.accountDefaults.business.confirmServicesDescription =
        !!input.accountDefaults.business.servicesDescription;
    }

    // Customize fees
    if (input.accountDefaults?.fees) {
      const feesUpdate = {...input.accountDefaults.fees} as ExtendedAccountFees;
      const customizePlan =
        input.accountDefaults.fees.dailyExtraSeatsFee !== undefined ||
        input.accountDefaults.fees.dailyPlatformFee !== undefined;
      if (!customizePlan) {
        feesUpdate.dailyExtraSeatsFee = null;
        feesUpdate.dailyPlatformFee = null;
      }
      const customizeSteps = Boolean(input.accountDefaults.fees.steps);
      if (!customizeSteps) {
        feesUpdate.steps = null;
      }
      feesUpdate.extra = feesUpdate.extra?.map((extra) => ({
        ...extra,
        // @ts-ignore
        region: checkIfRegional(extra.region) ? AccountExtraFeeRegion.ALL : extra.region
      }));
      if (!feesUpdate.highRiskFee) {
        feesUpdate.highRiskFee = null;
      }
      input.accountDefaults.fees = feesUpdate;
    }

    await updateMasterAccount({
      variables: {
        input: omitEmpty(input)
      }
    });
    setEditing(false);
  };

  return (
    <TableCard
      {...cardProps}
      title="Account defaults"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik<UpdateMasterAccountInput> onSubmit={handleSubmit} initialValues={initialValues}>
          {() => {
            return (
              <Form {...formProps} style={{padding: 12}}>
                <MasterAccountDefaultsForm formProps={formProps} />
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  name="submit">
                  <Stack>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                    <SubmitButton>Submit</SubmitButton>
                  </Stack>
                  <ScrollToError />
                </FormItem>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <div>
          <div style={{padding: 12, borderBottom: '1px solid #e8e8e8'}}>
            <PropertyList
              omitEmpty
              labelWidth={280}
              properties={[
                {
                  label: 'Billing Plan',
                  value: masterAccount.accountDefaults?.billingPlan?.replace('_', ' ')
                },
                {
                  label: 'Business category',
                  value: masterAccount.accountDefaults?.business?.mcc
                    ? businessCategories[masterAccount.accountDefaults?.business?.mcc]?.description
                    : null
                },
                {
                  label: 'Website',
                  value: formatWebsite(masterAccount.accountDefaults?.business?.website)
                },
                {
                  label: 'Services Description',
                  value: masterAccount.accountDefaults?.business?.servicesDescription
                },
                {
                  label: 'Language',
                  value: formatLanguage(masterAccount.accountDefaults?.language)
                },
                {
                  label: 'Card transaction fee',
                  value: formatFlatFee(masterAccount.accountDefaults?.fees?.trxFee)
                },
                {
                  label: 'Flat fee per card transaction',
                  value: formatFlatFee(masterAccount.accountDefaults?.fees?.trxFlatFee)
                },
                {
                  label: 'Daily platform usage fee',
                  value: formatFlatFee(masterAccount.accountDefaults?.fees?.dailyPlatformFee)
                },
                {
                  label: 'Daily extra seat fee',
                  value: formatFlatFee(masterAccount.accountDefaults?.fees?.dailyExtraSeatsFee)
                },
                {
                  label: 'High-risk transaction surcharge',
                  value: formatFee(masterAccount.accountDefaults?.fees?.highRiskFee)
                },
                ...(masterAccount.accountDefaults?.fees?.extra || [])
                  .map((fee) => ({
                    label: fee!.name!,
                    value: formatExtraFee(fee)
                  }))
                  .sort((a, b) => (a.label < b.label ? -1 : 1))
              ]}
            />
          </div>
          {masterAccount.accountDefaults?.fees?.steps && (
            <Table
              dataSource={masterAccount.accountDefaults?.fees?.steps!}
              columns={stepsColumns}
              rowKey="from"
              pagination={false}
            />
          )}
        </div>
      )}
    </TableCard>
  );
};
